<div class="space-y-4">
  @for (q of questions; track q.id) {
    <div class="card card-padding">
      <!-- Question header: color swatch + text + badges -->
      @if (!hideHeader) {
        <div class="flex flex-wrap items-start gap-2 mb-3">
          <div class="flex items-center gap-2 flex-1 min-w-0">
            <span
              class="inline-block w-3.5 h-3.5 rounded-sm shrink-0"
              [style.backgroundColor]="swatchColor(q.id!)"
              title="Annotation color for this question"
            ></span>
            <span
              class="min-w-0 text-base font-semibold text-slate-800 break-words whitespace-normal"
              >{{ q.text }}</span
            >
          </div>
          <div class="flex flex-wrap items-center gap-1.5 shrink-0">
            <span class="badge-neutral">{{ typeLabel(q.type) }}</span>
            @if (q.required) {
              <span class="badge-danger" title="Required">Required</span>
            }
            @if (q.required && !annotatedQuestionIds.has(q.id!) && !isSkipped(q.id!)) {
              <span
                class="badge-warning"
                data-testid="evidence-required"
                title="Evidence annotation required"
                >Evidence required</span
              >
            }
          </div>
        </div>
      }

      @if (isSkipped(q.id!)) {
        <p class="text-sm italic text-slate-500" data-testid="skipped-chip">
          No answer found in paper
        </p>
      } @else {
        @switch (q.type) {
          <!-- BOOLEAN -->
          @case (QuestionType.BOOLEAN) {
            <div class="flex gap-4">
              <label class="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="radio"
                  class="w-4 h-4 accent-secondary cursor-pointer"
                  [name]="instanceId + '-q' + q.id"
                  value="true"
                  [checked]="getAnswer(q.id!) === 'true'"
                  [disabled]="readOnly || saving"
                  (change)="onBooleanChange(q.id!, 'true')"
                  data-testid="boolean-yes"
                />
                Yes
              </label>
              <label class="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="radio"
                  class="w-4 h-4 accent-secondary cursor-pointer"
                  [name]="instanceId + '-q' + q.id"
                  value="false"
                  [checked]="getAnswer(q.id!) === 'false'"
                  [disabled]="readOnly || saving"
                  (change)="onBooleanChange(q.id!, 'false')"
                  data-testid="boolean-no"
                />
                No
              </label>
            </div>
          }
          <!-- SINGLE_CHOICE -->
          @case (QuestionType.SINGLE_CHOICE) {
            <div class="flex flex-col gap-2">
              @for (opt of q.options; track opt) {
                <label class="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    class="w-4 h-4 accent-secondary cursor-pointer shrink-0 mt-0.5"
                    [name]="instanceId + '-q' + q.id"
                    [value]="opt"
                    [checked]="getAnswer(q.id!) === opt"
                    [disabled]="readOnly || saving"
                    (change)="onSingleChoiceChange(q.id!, opt)"
                  />
                  <span class="break-words whitespace-normal">{{ opt }}</span>
                </label>
              }
            </div>
          }
          <!-- MULTI_CHOICE -->
          @case (QuestionType.MULTI_CHOICE) {
            <div class="flex flex-col gap-2">
              @for (opt of q.options; track opt) {
                <label class="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    class="w-4 h-4 accent-secondary cursor-pointer shrink-0 mt-0.5"
                    [checked]="isChecked(q.id!, opt)"
                    [disabled]="readOnly || saving"
                    (change)="onMultiChoiceChange(q.id!, opt, $any($event.target).checked)"
                  />
                  <span class="break-words whitespace-normal">{{ opt }}</span>
                </label>
              }
            </div>
          }
          <!-- FULL_TEXT -->
          @case (QuestionType.FULL_TEXT) {
            <textarea
              class="input-field resize-y"
              rows="3"
              [value]="getAnswer(q.id!)"
              [disabled]="readOnly || saving"
              (input)="onTextChange(q.id!, $any($event.target).value)"
            ></textarea>
          }
          <!-- NUMERIC -->
          @case (QuestionType.NUMERIC) {
            <input
              type="number"
              class="input-field"
              [value]="getAnswer(q.id!)"
              [disabled]="readOnly || saving"
              (input)="onNumericChange(q.id!, $any($event.target).value)"
            />
          }
        }
      }

      @let ocrAnns = annotationsFor(q.id!);
      @if (ocrAnns.some((a) => a.ocr_text)) {
        <div class="mt-3 border-t border-slate-100 pt-3">
          <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
            OCR Extracted Text
          </p>
          <div class="space-y-1">
            @for (a of ocrAnns; track a.id) {
              @if (a.ocr_text; as text) {
                @if (editingOcrId === a.id) {
                  <div class="flex items-start gap-2 text-xs">
                    <span class="text-slate-400 font-mono shrink-0 mt-1">p.{{ a.page }}</span>
                    <textarea
                      #ocrEdit
                      class="input-field resize-y flex-1 font-mono text-xs"
                      rows="3"
                      [value]="text"
                    ></textarea>
                    <div class="flex flex-col gap-1.5 shrink-0">
                      <button
                        class="btn-small-primary"
                        (click)="saveEditedOcr(a.id, ocrEdit.value)"
                        data-testid="ocr-save"
                      >
                        Save
                      </button>
                      <button
                        class="btn-small-secondary"
                        (click)="cancelEditOcr()"
                        data-testid="ocr-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                } @else {
                  <div
                    class="flex items-start gap-1.5 text-xs group cursor-pointer rounded px-1 -mx-1 hover:bg-slate-50"
                    (click)="ocrTextClick.emit({ annotationId: a.id, page: a.page })"
                    title="Show on PDF"
                  >
                    <span class="text-slate-400 font-mono shrink-0 mt-0.5">p.{{ a.page }}</span>
                    <span class="text-slate-600 break-words whitespace-normal flex-1 min-w-0">{{
                      text
                    }}</span>
                    <button
                      class="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                      (click)="$event.stopPropagation(); copyOcrText(text)"
                      title="Copy text"
                      data-testid="ocr-copy"
                    >
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                    @if (!readOnly) {
                      <button
                        class="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                        (click)="$event.stopPropagation(); startEditOcr(a.id)"
                        title="Edit text"
                        data-testid="ocr-edit"
                      >
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    }
                  </div>
                }
              }
            }
          </div>
        </div>
      }

      @if (q.required && !readOnly) {
        <label class="mt-3 flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            class="w-4 h-4 accent-secondary cursor-pointer"
            [checked]="isSkipped(q.id!)"
            [disabled]="saving"
            (change)="onSkipToggle(q.id!, $any($event.target).checked)"
            data-testid="skip-checkbox"
          />
          No answer can be found in this paper
        </label>
      }
    </div>
  }
</div>
